// Global Types
import type {
  $WithRouter,
} from '@vroskus/library-navigation';
import type {
  $Children,
} from 'common.types';

// Global Enums
import {
  AccessTokenKey,
} from 'common.types';

// Global Components
import * as React from 'react';

// Helpers
import ErrorLog from '@vroskus/library-sentry/dist/browser';

// Components
import Loader from '../Loader';

// Services
import Services from '../../../services';

// Types
import type {
  $GetRegionsAction,
  $RedirectAction,
  $SetupLanguageAction,
  $SetupRegionAction,
  $SetupUserAction,
} from '../../../redux/modules/main/actions';
import type {
  $SetAuthUserAction,
  $SetupAuthenticationAction,
} from '../../../redux/modules/auth/actions';

export type $OwnProps = $WithRouter & {
  children: $Children;
};

export type $StateProps = Record<string, never>;

export type $DispatchProps = {
  getRegions: $GetRegionsAction;
  redirect: $RedirectAction;
  setAuthUser: $SetAuthUserAction;
  setupAuthentication: $SetupAuthenticationAction;
  setupLanguage: $SetupLanguageAction;
  setupRegion: $SetupRegionAction;
  setupUser: $SetupUserAction;
};

type $Props = $DispatchProps & $OwnProps & $StateProps;

type $State = {
  initialised: boolean;
};

class MainView extends React.Component<$Props, $State> {
  constructor(props: $Props) {
    super(props);

    this.state = {
      initialised: false,
    };
  }

  componentDidMount(): void {
    this.init();
    this.setupSocket();
  }

  componentDidCatch(error: Error): void {
    ErrorLog.exception(error as Error);
  }

  setupSocket(): void {
    const {
      getRegions,
    } = this.props;

    Services.Socket.on({
      action: (data) => {
        if (data.modelName === 'Region') {
          getRegions();
        }
      },
      type: Services.Socket.type.modelChangedEvent,
    });
  }

  async setupRegionsData(): Promise<void> {
    const {
      getRegions,
      setupRegion,
    } = this.props;

    try {
      await getRegions();
      await setupRegion();
    } catch (err) {
      const error = err as Error;

      // this.setState({
      //   error,
      // });

      ErrorLog.exception(error);
    }
  }

  async setupAuthData(): Promise<void> {
    const {
      location,
      redirect,
      setAuthUser,
      setupAuthentication,
      setupUser,
    } = this.props;

    try {
      Services.Api.setUnauthenticatedHandler({
        handler: async () => {
          const pathname = '/login';

          if (location.pathname !== pathname) {
            await setAuthUser(null);

            redirect({
              pathname,
              state: {
                from: location,
              },
            });
          }
        },
      });

      const accessToken: string | void = Services.Cookies.getItem(AccessTokenKey.crewmember);

      if (accessToken) {
        const {
          value: user,
        } = await setupAuthentication();

        if (user !== null) {
          await setupUser(user);
        }
      }
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async setupSavedData(): Promise<void> {
    const {
      setupLanguage,
    } = this.props;

    try {
      await setupLanguage();
    } catch (err) {
      const error = err as Error;

      ErrorLog.exception(error);
    }
  }

  async init(): Promise<void> {
    await this.setupRegionsData();
    await this.setupSavedData();
    await this.setupAuthData();

    this.setState({
      initialised: true,
    });
  }

  render() {
    const {
      children,
    } = this.props;
    const {
      initialised,
    } = this.state;

    return (
      <Loader loading={initialised === false}>
        <React.Suspense
          fallback={(
            <div />
          )}
        >
          {children}
        </React.Suspense>
      </Loader>
    );
  }
}

export default MainView;
